// dashboard/src/components/AdminDonorImages.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE } from '../api';
import ImageCropper from './ImageCropper';

function AdminDonorImages() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  
  // Image cropping states
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [croppedImageBlob, setCroppedImageBlob] = useState(null);
  
  // A4 Portrait aspect ratio (210mm x 297mm) = 0.707
  const A4_ASPECT_RATIO = 210 / 297;

  // Get authentication info
  const token = localStorage.getItem('token');
  const storedUser = localStorage.getItem('dashboardUser');
  const adminUser = storedUser ? JSON.parse(storedUser) : null;
  const userRole = adminUser?.role || '';

  // Check if user can manage donor images
  const canManage = ['admin', 'master_admin', 'graphics'].includes(userRole);

  // Fetch existing daily donor images
  const fetchImages = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/daily-donor-images`);
      
      if (res.data.success) {
        setImages(res.data.images);
      } else {
        console.warn('Unexpected response format:', res.data);
        setImages(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      console.error('Error fetching images:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please log in again.');
      } else {
        alert('Failed to fetch donor images.');
      }
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  // Handle file selection and trigger cropping
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }

      // Create image URL for cropping
      const imageUrl = URL.createObjectURL(file);
      setImageToCrop(imageUrl);
      setSelectedFile(file);
      setShowCropper(true);
    }
  };

  // Handle crop completion
  const handleCropComplete = (croppedBlob) => {
    setCroppedImageBlob(croppedBlob);
    setShowCropper(false);
    // Clean up the temporary URL
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
    }
  };

  // Handle crop cancellation
  const handleCropCancel = () => {
    setShowCropper(false);
    setSelectedFile(null);
    setCroppedImageBlob(null);
    // Clean up the temporary URL
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
    }
    setImageToCrop(null);
    
    // Clear the file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  };

  // Handle description change
  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
  };

  // Upload new image
  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!croppedImageBlob) {
      alert('Please select and crop an image first');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      // Create a file from the cropped blob with proper naming
      const croppedFile = new File([croppedImageBlob], selectedFile.name, {
        type: selectedFile.type,
        lastModified: Date.now(),
      });
      
      formData.append('image', croppedFile);
      formData.append('description', description);

      const response = await axios.post(
        `${API_BASE}/api/daily-donor-images/upload`, 
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        alert('Image uploaded successfully!');
        setSelectedFile(null);
        setCroppedImageBlob(null);
        setDescription('');
        e.target.reset();
        fetchImages();
      } else {
        alert(response.data.error || 'Upload failed');
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      
      if (error.response?.status === 401) {
        alert('Authentication failed. Please log in again.');
      } else if (error.response?.status === 403) {
        alert('Access denied. You do not have permission to upload images.');
      } else if (error.response?.status === 413) {
        alert('File too large. Maximum size is 10MB.');
      } else {
        const errorMessage = error.response?.data?.error || error.message;
        alert(`Error uploading image: ${errorMessage}`);
      }
    } finally {
      setUploading(false);
    }
  };

  // Delete an image
  const handleDelete = async (imageId, filename) => {
    if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
      return;
    }

    try {
      const response = await axios.delete(
        `${API_BASE}/api/daily-donor-images/${imageId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        alert('Image deleted successfully!');
        fetchImages();
      } else {
        alert(response.data.error || 'Delete failed');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image. Please try again.');
    }
  };

  // Start editing description
  const startEditing = (image) => {
    setEditingImage({ ...image, newDescription: image.description });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingImage(null);
  };

  // Save edited description
  const saveEdit = async () => {
    try {
      const response = await axios.patch(
        `${API_BASE}/api/daily-donor-images/${editingImage._id}`,
        { description: editingImage.newDescription },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        alert('Description updated successfully!');
        setEditingImage(null);
        fetchImages();
      } else {
        alert(response.data.error || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating description:', error);
      alert('Failed to update description.');
    }
  };

  // Toggle active status
  const toggleActive = async (imageId, currentStatus) => {
    try {
      const response = await axios.patch(
        `${API_BASE}/api/daily-donor-images/${imageId}`,
        { isActive: !currentStatus },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        alert(`Image ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
        fetchImages();
      } else {
        alert(response.data.error || 'Update failed');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Failed to update image status.');
    }
  };

  if (!canManage) {
    return (
      <div className="p-4">
        <h2 className="text-2xl font-bold mb-4">Daily Donor Images</h2>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <strong>Access Denied:</strong> You do not have permission to manage daily donor images.
          Only master_admin, admin, and graphics roles can access this feature.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Daily Donor Images</h2>
      <p className="mb-6 text-gray-600">
        Upload and manage images for daily donors that will be displayed on the website homepage.
        Only master_admin, admin, and graphics roles can manage these images.
      </p>

      {/* Debug Information */}
      <div className="mb-6 p-3 bg-gray-100 rounded text-sm">
        <strong>Current User Info:</strong>
        <br />
        Role: {userRole || 'Not detected'}
        <br />
        Token Present: {token ? 'Yes' : 'No'}
        <br />
        Can Manage: {canManage ? 'Yes' : 'No'}
        <br />
        Total Images: {images.length}
      </div>

      {/* Upload Form */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h3 className="text-lg font-semibold mb-4">Upload New Image</h3>
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-medium text-blue-800 mb-2">📄 A4 Portrait Format Required</h4>
          <p className="text-sm text-blue-700">
            Images will be automatically cropped to A4 portrait format (210×297mm ratio) 
            to ensure consistent display across the website.
          </p>
        </div>
        
        <form onSubmit={handleUpload} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              required
            />
            {croppedImageBlob && (
              <div className="mt-2 text-sm text-green-600">
                ✅ Image cropped and ready for upload
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={handleDescriptionChange}
              placeholder="Enter a description for this image..."
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              rows="3"
            />
          </div>

          <button
            type="submit"
            disabled={uploading || !croppedImageBlob}
            className={`px-4 py-2 rounded-md text-white font-medium ${
              uploading || !croppedImageBlob
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {uploading ? 'Uploading...' : 'Upload Cropped Image'}
          </button>
        </form>
      </div>

      {/* Images Grid */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Existing Images ({images.length})</h3>
        
        {images.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No daily donor images found.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {images.map((img) => (
              <div key={img._id} className="border rounded-lg overflow-hidden shadow-sm">
                <div className="relative w-full" style={{ aspectRatio: '210/297' }}>
                  <img
                    src={`${API_BASE}/${img.path}`}
                    alt={img.originalName}
                    className="w-full h-full object-cover"
                  />
                  <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${
                    img.isActive ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                  }`}>
                    {img.isActive ? 'Active' : 'Inactive'}
                  </div>
                </div>
                
                <div className="p-4">
                  <h4 className="font-medium text-sm text-gray-900 mb-2">
                    {img.originalName}
                  </h4>
                  
                  <div className="mb-3">
                    {editingImage && editingImage._id === img._id ? (
                      <div className="space-y-2">
                        <textarea
                          value={editingImage.newDescription}
                          onChange={(e) => setEditingImage({
                            ...editingImage,
                            newDescription: e.target.value
                          })}
                          className="w-full p-2 text-xs border rounded"
                          rows="2"
                          placeholder="Enter description..."
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={saveEdit}
                            className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <p className="text-xs text-gray-600">
                          <strong>Description:</strong> {img.description || 'No description'}
                        </p>
                        <button
                          onClick={() => startEditing(img)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          Edit Description
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className="text-xs text-gray-500 mb-3">
                    <p><strong>Uploaded:</strong> {new Date(img.createdAt).toLocaleString()}</p>
                    <p><strong>By:</strong> {img.uploadedBy?.username || 'Unknown'}</p>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => toggleActive(img._id, img.isActive)}
                      className={`px-3 py-1 text-xs rounded font-medium ${
                        img.isActive
                          ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                          : 'bg-green-100 text-green-800 hover:bg-green-200'
                      }`}
                    >
                      {img.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    
                    <button
                      onClick={() => handleDelete(img._id, img.originalName)}
                      className="px-3 py-1 bg-red-100 text-red-800 text-xs rounded font-medium hover:bg-red-200"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Image Cropper Modal */}
      {showCropper && (
        <ImageCropper
          imageSrc={imageToCrop}
          aspect={A4_ASPECT_RATIO}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
}

export default AdminDonorImages;
